import React, { Suspense, lazy, Component } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, MotionConfig } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import { Link } from "react-scroll";
import { FaArrowUp } from "react-icons/fa6";
import { ThemeProvider } from "./ThemeContext";
import { PortfolioDataProvider, usePortfolioData } from "./PortfolioDataContext";
import { OWNER_NAME, defaultPortfolioData } from "./services/seedData";
import { useScrollY } from "./hooks/useScrollY";

import "./assets/css/bootstrap.css";
import "./App.css";

import Navbar from "./components/Navbar";
import Login from "./components/Login";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy-load everything below the fold + heavy canvas
const GalaxyCanvas = lazy(() => import("./components/GalaxyCanvas"));
const About = lazy(() => import("./components/About"));
const Portfolio = lazy(() => import("./components/Portfolio"));
const Blog = lazy(() => import("./components/Blog"));
const Contact = lazy(() => import("./components/Contact"));
const Footer = lazy(() => import("./components/Footer"));
// Admin panel is lazy-loaded (heavy PDF parser)
const AdminUpload = lazy(() => import("./components/AdminUpload"));

const AdminFallback = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "var(--bg)",
      color: "var(--text-muted)",
      fontFamily: "var(--font-heading)",
      fontSize: 14,
      letterSpacing: "0.1em",
    }}
  >
    Loading…
  </div>
);

class ErrorBoundary extends Component {
  state = { hasError: false, message: "" };
  static getDerivedStateFromError(err) {
    return { hasError: true, message: err?.message || "Unknown error" };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--bg)",
            color: "var(--text-muted)",
            fontFamily: "var(--font-heading)",
            fontSize: 14,
            gap: 12,
          }}
        >
          <span style={{ fontSize: 32 }}>⚠️</span>
          <p style={{ margin: 0 }}>Something went wrong.</p>
          <p style={{ margin: 0, opacity: 0.6, fontSize: 12 }}>{this.state.message}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 8, padding: "6px 16px", borderRadius: 8,
              background: "var(--purple)", color: "#fff", border: "none", cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Portfolio Home ────────────────────────────────────────────
const PortfolioHome = () => {
  const { data } = usePortfolioData();
  const scrollY = useScrollY();
  const showBack = scrollY > 200;
  const ownerName = data?.profile?.name || OWNER_NAME;
  const roles = data?.profile?.roles;
  const typeSequence = (roles?.length ? roles : defaultPortfolioData.profile.roles)
    .flatMap((r) => [r, 2000]);
  return (
    <>
      {/* BACK TO TOP */}
      <button
        className={`btn-back_to_top ${showBack ? "visible" : ""}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label="Back to top"
      >
        <FaArrowUp />
      </button>

      {/* FIXED NAVBAR */}
      <Navbar />

      {/* HERO */}
      <section className="hero-section" id="home">
        <div className="hero-canvas">
          <Suspense fallback={null}>
            <GalaxyCanvas />
          </Suspense>
        </div>
        <div className="hero-overlay" />

        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.6, 0.01, 0.05, 0.95] }}
        >
          <motion.span
            className="hero-eyebrow"
            initial={{ opacity: 0, letterSpacing: "0.6em" }}
            animate={{ opacity: 0.85, letterSpacing: "0.35em" }}
            transition={{ delay: 0.3, duration: 1.1 }}
          >
            Welcome to my universe
          </motion.span>

          <motion.h1
            className="hero-name"
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.55, duration: 0.8, ease: "easeOut" }}
          >
            {ownerName}
          </motion.h1>

          <motion.div
            className="hero-typewriter"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.6 }}
          >
            <TypeAnimation
              key={typeSequence.join("|")}
              sequence={typeSequence}
              wrapper="span"
              speed={55}
              repeat={Infinity}
            />
          </motion.div>
        </motion.div>

        {/* CTA group: direct child of hero-section (NOT inside any motion.div)
            so backdrop-filter can composite against the canvas layer */}
        <div className="hero-cta-group">
          <Link to="about" href="#about" smooth duration={800}>
            <button className="btn-glow">Explore My Work</button>
          </Link>
          <Link to="contact" href="#contact" smooth duration={800}>
            <button className="btn-outline-glow">Get In Touch</button>
          </Link>
        </div>

        <Link to="about" href="#about" smooth duration={800}>
          <div className="hero-scroll" aria-label="Scroll down">
            <div className="hero-scroll-line" />
            <span>Scroll</span>
          </div>
        </Link>
      </section>

      <Suspense fallback={null}>
        <About />
        <Portfolio />
        <Blog />
        <Contact />
        <Footer />
      </Suspense>
    </>
  );
};

// ── Root App with Router ──────────────────────────────────────
const App = () => (
  <MotionConfig reducedMotion="user">
  <ThemeProvider>
    <PortfolioDataProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<ErrorBoundary><PortfolioHome /></ErrorBoundary>} />
          <Route path="/login" element={<ErrorBoundary><Login /></ErrorBoundary>} />
          <Route
            path="/admin"
            element={
              <ErrorBoundary>
                <ProtectedRoute>
                  <Suspense fallback={<AdminFallback />}>
                    <AdminUpload />
                  </Suspense>
                </ProtectedRoute>
              </ErrorBoundary>
            }
          />
        </Routes>
      </BrowserRouter>
    </PortfolioDataProvider>
  </ThemeProvider>
  </MotionConfig>
);

export default App;
